import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, LinearProgress, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, LocalShipping, Print, QrCodeScanner, Scale, Undo, Visibility } from '@mui/icons-material';
import api from '../../services/api';
import type {
  CreatePallet, Machine, MixBatch, ProductLot, ProductLotConfig, ProductLotTrace, Shift, StorageLocation, WeighIntoLot,
} from '../../types/factory';
import type { Recipe } from '../../types';
import {
  EmptyState, Kpi, KpiStrip, PageHeader, ProductSelect, Section, StatusPill, addDaysIso, errorMessage, fmtQty, num, todayIso, useProducts, usePageView,
} from './shared';
import type { PageView } from './shared';
import { TraceDialog } from './FactoryMaterialsPage';
import { useSerialScale } from './plant/serialScale';
import CustomerPicker, { PickedCustomer } from '../../components/commercial/CustomerPicker';
import { fmtDateTime, printLotLabels } from './lotLabels';

// Lotes y pallets (reunión con Asener 03/11/2026): a la salida de la inyectora se pesan las bolsas y cada pesada
// suma a un lote de peso fijo que se cierra solo y se etiqueta; la máquina de O-ring / la etiquetadora QR arma
// pallets de producto terminado con esos lotes. Con el código de barras de un pallet se llega hasta la mezcla.

const SHIFTS: { value: Shift; label: string }[] = [
  { value: 'TM', label: 'Mañana' },
  { value: 'TT', label: 'Tarde' },
  { value: 'TN', label: 'Noche' },
];

/** Turno según la hora de Argentina: 6 a 14 mañana, 14 a 22 tarde, el resto noche. */
function currentShift(): Shift {
  const h = Number(new Date().toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', hour12: false }));
  return h >= 6 && h < 14 ? 'TM' : h >= 14 && h < 22 ? 'TT' : 'TN';
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral'> = {
  Abierto: 'info', Cerrado: 'ok', Consumido: 'neutral', Despachado: 'neutral',
};

const isMold = (m: Machine) => m.kind === 'Matriz';
const kg = (n?: number | null) => (n == null ? '—' : `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(n)} kg`);

const VIEWS: PageView[] = [
  { key: 'lotes', label: 'Pesadas y lotes' },
  { key: 'pallets', label: 'Pallets', description: 'Producto terminado armado con lotes de insumo: dónde está cada pallet y a qué cliente fue.' },
  { key: 'config', label: 'Productos que se lotean', description: 'Peso fijo del lote, peso de cada pieza, unidades por bolsa y qué lleva cada producto terminado.' },
];

const FactoryProductLotsPage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [configs, setConfigs] = useState<ProductLotConfig[]>([]);
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [lots, setLots] = useState<ProductLot[] | null>(null);
  const [pallets, setPallets] = useState<ProductLot[] | null>(null);
  const [weighing, setWeighing] = useState(false);
  const [trial, setTrial] = useState(false);
  const [palletOpen, setPalletOpen] = useState(false);
  const [trace, setTrace] = useState<ProductLotTrace | null>(null);
  const [dispatching, setDispatching] = useState<ProductLot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lotFilter, setLotFilter] = useState<'activos' | 'todos'>('activos');
  const [palletFilter, setPalletFilter] = useState<'deposito' | 'todos'>('deposito');

  const loadRefs = useCallback(async () => {
    const [m, c, l] = await Promise.all([api.getMachines(), api.getLotConfigs(), api.getStorageLocations()]);
    setMachines(m);
    setConfigs(c);
    setLocations(l.filter(x => x.isActive));
  }, []);
  const loadLots = useCallback(async () => {
    const from = addDaysIso(todayIso(), -60);
    const [ls, ps] = await Promise.all([
      api.getProductLots({ kind: 'Insumo', from: lotFilter === 'todos' ? from : undefined, available: lotFilter === 'activos' ? true : undefined }),
      api.getProductLots({ kind: 'Pallet', status: palletFilter === 'deposito' ? 'Cerrado' : undefined, from: palletFilter === 'todos' ? from : undefined }),
    ]);
    setLots(ls);
    setPallets(ps);
  }, [lotFilter, palletFilter]);
  useEffect(() => { loadRefs().catch(e => setError(errorMessage(e, 'No se pudieron cargar las máquinas'))); }, [loadRefs]);
  useEffect(() => { loadLots().catch(e => setError(errorMessage(e, 'No se pudieron cargar los lotes'))); }, [loadLots]);

  // En Configuración no van el escáner, los indicadores ni los botones de pesar/armar.
  const operational = !(flat && tab === 2);

  const openTrace = async (id: number) => {
    try { setTrace(await api.traceProductLot(id)); } catch (e) { setError(errorMessage(e, 'No se pudo abrir el lote')); }
  };

  const openLots = (lots || []).filter(l => l.status === 'Abierto');
  const closedAvailable = (lots || []).filter(l => l.status === 'Cerrado');
  const inStock = (pallets || []).filter(p => p.status === 'Cerrado');

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Lotes y pallets'}
        description={flat && current.description ? current.description : <>Las bolsas se pesan a la salida de la máquina y suman al lote abierto, que se cierra solo al llegar a su peso. El pallet de producto terminado consume esos lotes, y con su código se llega hasta la máquina, la matriz y la mezcla.</>}
        actions={operational && <>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setPalletOpen(true)}>Armar pallet</Button>
          <Button variant="contained" startIcon={<Scale />} onClick={() => setWeighing(true)} disabled={configs.length === 0}>Pesar bolsa</Button>
        </>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {operational && <ScanBox onFound={setTrace} />}
      {operational && <KpiStrip>
        <Kpi label="Lotes abiertos" value={fmtQty(openLots.length)} hint={openLots.length ? openLots.map(l => l.machineName).filter(Boolean).join(', ') : 'Ninguna máquina pesando'} />
        <Kpi label="Lotes cerrados sin usar" value={fmtQty(closedAvailable.length)} hint={`${fmtQty(closedAvailable.reduce((s, l) => s + l.remainingUnits, 0))} u. de insumo`} />
        <Kpi label="Pallets en depósito" value={fmtQty(inStock.length)} hint={`${fmtQty(inStock.reduce((s, l) => s + l.units, 0))} u. terminadas`} />
        <Kpi label="Productos que se lotean" value={fmtQty(configs.filter(c => c.isActive).length)} hint={configs.length ? undefined : 'Se configuran en Configuración → Productos que se lotean'} tone={configs.length ? undefined : 'warn'} />
      </KpiStrip>}

      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Lotes de insumo" />
        <Tab label="Pallets" />
        <Tab label="Configuración" />
      </Tabs>)}

      {tab === 0 && (
        <Section
          title="Lotes de insumo"
          description="Uno por máquina, matriz y mezcla. El de prueba es para tiradas especiales: lleva su propio peso."
          actions={<Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={lotFilter} onChange={(_, v) => v && setLotFilter(v)}>
              <ToggleButton value="activos">Con unidades</ToggleButton>
              <ToggleButton value="todos">Últimos 60 días</ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" onClick={() => setTrial(true)}>Lote de prueba</Button>
          </Stack>}
        >
          <LotsTable lots={lots} onTrace={openTrace} onChanged={loadLots} onError={setError} />
        </Section>
      )}

      {tab === 1 && (
        <Section
          title="Pallets de producto terminado"
          description="Cada pallet lleva los lotes que consumió. Al despacharlo queda asentado a qué cliente fue."
          actions={<ToggleButtonGroup size="small" exclusive value={palletFilter} onChange={(_, v) => v && setPalletFilter(v)}>
            <ToggleButton value="deposito">En depósito</ToggleButton>
            <ToggleButton value="todos">Últimos 60 días</ToggleButton>
          </ToggleButtonGroup>}
        >
          <PalletsTable pallets={pallets} onTrace={openTrace} onDispatch={setDispatching} onChanged={loadLots} onError={setError} />
        </Section>
      )}

      {tab === 2 && <ConfigTab configs={configs} reload={loadRefs} />}

      {weighing && (
        <WeighDialog
          machines={machines} configs={configs} openLots={openLots}
          onClose={() => setWeighing(false)} onSaved={() => loadLots()}
        />
      )}
      {trial && <TrialDialog machines={machines} configs={configs} onClose={() => setTrial(false)} onSaved={() => { setTrial(false); loadLots(); }} />}
      {palletOpen && (
        <PalletDialog
          machines={machines} configs={configs} locations={locations}
          onClose={() => setPalletOpen(false)}
          onSaved={t => { setPalletOpen(false); loadLots(); setTrace(t); printLotLabels([t.lot]); }}
        />
      )}
      {dispatching && (
        <DispatchDialog pallet={dispatching} onClose={() => setDispatching(null)} onSaved={() => { setDispatching(null); loadLots(); }} />
      )}
      {trace && <LotTraceDialog trace={trace} onClose={() => setTrace(null)} onOpen={openTrace} onChanged={() => loadLots()} />}
    </Box>
  );
};

// ==================== Escaneo ====================

/** El lector de código de barras escribe como un teclado y termina con Enter. */
const ScanBox: React.FC<{ onFound: (t: ProductLotTrace) => void }> = ({ onFound }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const search = async () => {
    if (!code.trim()) return;
    try {
      onFound(await api.traceProductLotByCode(code));
      setCode('');
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'No se encontró ese código'));
    }
  };
  return (
    <TextField
      size="small" fullWidth value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }}
      placeholder="Escaneá o escribí el código de un lote o pallet (L-031126-INY3-01, P-031126-02…)"
      error={!!error} helperText={error || ' '} sx={{ mb: 1 }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><QrCodeScanner fontSize="small" /></InputAdornment>,
        endAdornment: <InputAdornment position="end"><Button size="small" onClick={search} disabled={!code.trim()}>Buscar</Button></InputAdornment>,
      }}
    />
  );
};

// ==================== Tablas ====================

const LotsTable: React.FC<{ lots: ProductLot[] | null; onTrace: (id: number) => void; onChanged: () => void; onError: (m: string) => void }> = ({ lots, onTrace, onChanged, onError }) => {
  if (!lots) return <LinearProgress />;
  if (lots.length === 0) return <EmptyState title="No hay lotes" hint="Se crean solos con la primera bolsa que se pesa." />;
  const close = async (l: ProductLot) => {
    if (!window.confirm(`¿Cerrar el lote ${l.code} con ${kg(l.kg)}? La próxima pesada abre uno nuevo.`)) return;
    try { const closed = await api.closeProductLot(l.id); onChanged(); printLotLabels([closed]); } catch (e) { onError(errorMessage(e, 'No se pudo cerrar')); }
  };
  const remove = async (l: ProductLot) => {
    if (!window.confirm(`¿Borrar el lote vacío ${l.code}?`)) return;
    try { await api.deleteProductLot(l.id); onChanged(); } catch (e) { onError(errorMessage(e, 'No se pudo borrar')); }
  };
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Código</TableCell>
            <TableCell>Producto</TableCell>
            <TableCell>Máquina · matriz</TableCell>
            <TableCell>Mezcla</TableCell>
            <TableCell sx={{ minWidth: 170 }}>Peso</TableCell>
            <TableCell align="right">Unidades</TableCell>
            <TableCell align="right">Sin usar</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {lots.map(l => (
            <TableRow key={l.id} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{l.code}</TableCell>
              <TableCell>{l.productName}{l.isTrial && <Typography variant="caption" color="warning.main" display="block">prueba{l.notes ? ` · ${l.notes}` : ''}</Typography>}</TableCell>
              <TableCell>{[l.machineName, l.moldName].filter(Boolean).join(' · ') || '—'}</TableCell>
              <TableCell>{l.mixBatchCode || <Typography variant="caption" color="text.secondary">sin mezcla</Typography>}</TableCell>
              <TableCell>
                {l.targetKg > 0 ? (
                  <Stack spacing={0.5}>
                    <Typography variant="caption">{kg(l.kg)} de {kg(l.targetKg)}</Typography>
                    <LinearProgress variant="determinate" value={Math.min(100, (l.progressPct ?? 0) * 100)} sx={{ height: 6, borderRadius: 3 }} />
                  </Stack>
                ) : <Typography variant="caption">{kg(l.kg)} · se cierra a mano</Typography>}
              </TableCell>
              <TableCell align="right">{fmtQty(l.units)}{l.bags != null && <Typography variant="caption" color="text.secondary" display="block">{l.bags.toLocaleString('es-AR')} {l.bagName || 'bolsa'}s</Typography>}</TableCell>
              <TableCell align="right">{fmtQty(l.remainingUnits)}</TableCell>
              <TableCell><StatusPill tone={STATUS_TONE[l.status]}>{l.status}</StatusPill></TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Ver trazabilidad"><IconButton size="small" onClick={() => onTrace(l.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Imprimir etiqueta"><IconButton size="small" onClick={() => printLotLabels([l])}><Print fontSize="small" /></IconButton></Tooltip>
                {l.status === 'Abierto' && l.units > 0 && <Button size="small" onClick={() => close(l)}>Cerrar</Button>}
                {l.status === 'Abierto' && l.weighingsCount === 0 && <IconButton size="small" onClick={() => remove(l)}><Delete fontSize="small" /></IconButton>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PalletsTable: React.FC<{
  pallets: ProductLot[] | null; onTrace: (id: number) => void; onDispatch: (p: ProductLot) => void; onChanged: () => void; onError: (m: string) => void;
}> = ({ pallets, onTrace, onDispatch, onChanged, onError }) => {
  if (!pallets) return <LinearProgress />;
  if (pallets.length === 0) return <EmptyState title="No hay pallets" hint="Armá uno con “Armar pallet”: consume los lotes de insumo." />;
  const undo = async (p: ProductLot) => {
    if (!window.confirm(`¿Deshacer el despacho de ${p.code}? Vuelve a su ubicación.`)) return;
    try { await api.undoDispatchPallet(p.id); onChanged(); } catch (e) { onError(errorMessage(e, 'No se pudo deshacer')); }
  };
  const remove = async (p: ProductLot) => {
    if (!window.confirm(`¿Anular el pallet ${p.code}? Los componentes vuelven a sus lotes y al stock.`)) return;
    try { await api.deletePallet(p.id); onChanged(); } catch (e) { onError(errorMessage(e, 'No se pudo anular')); }
  };
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Código</TableCell>
            <TableCell>Producto</TableCell>
            <TableCell align="right">Unidades</TableCell>
            <TableCell>Armado</TableCell>
            <TableCell>Ubicación</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {pallets.map(p => (
            <TableRow key={p.id} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.code}</TableCell>
              <TableCell>{p.productName}</TableCell>
              <TableCell align="right">{fmtQty(p.units)}{p.scrapUnits > 0 && <Typography variant="caption" color="text.secondary" display="block">{fmtQty(p.scrapUnits)} rotos</Typography>}</TableCell>
              <TableCell>{fmtDateTime(p.openedAt)}{p.machineName && <Typography variant="caption" color="text.secondary" display="block">{p.machineName}{p.shift ? ` · ${p.shift}` : ''}</Typography>}</TableCell>
              <TableCell>{p.locationCode || '—'}</TableCell>
              <TableCell>
                <StatusPill tone={STATUS_TONE[p.status]}>{p.status === 'Cerrado' ? 'En depósito' : p.status}</StatusPill>
                {p.customerName && <Typography variant="caption" color="text.secondary" display="block">{p.customerName}</Typography>}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Ver trazabilidad"><IconButton size="small" onClick={() => onTrace(p.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Imprimir etiqueta"><IconButton size="small" onClick={() => printLotLabels([p])}><Print fontSize="small" /></IconButton></Tooltip>
                {p.status === 'Cerrado' && <Tooltip title="Despachar"><IconButton size="small" onClick={() => onDispatch(p)}><LocalShipping fontSize="small" /></IconButton></Tooltip>}
                {p.status === 'Despachado' && <Tooltip title="Deshacer despacho"><IconButton size="small" onClick={() => undo(p)}><Undo fontSize="small" /></IconButton></Tooltip>}
                {p.status === 'Cerrado' && <Tooltip title="Anular pallet"><IconButton size="small" onClick={() => remove(p)}><Delete fontSize="small" /></IconButton></Tooltip>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ==================== Pesar ====================

const ConfigSelect: React.FC<{ configs: ProductLotConfig[]; value: number | null; onChange: (id: number | null) => void }> = ({ configs, value, onChange }) => (
  <TextField size="small" select label="Producto que sale de la máquina" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} required>
    {configs.filter(c => c.isActive).map(c => <MenuItem key={c.productId} value={c.productId}>{c.productName}</MenuItem>)}
  </TextField>
);

const MachineFields: React.FC<{
  machines: Machine[]; machineId: number | null; moldId: number | null; mixBatchId: number | null; mixes: MixBatch[];
  onChange: (p: { machineId?: number | null; moldId?: number | null; mixBatchId?: number | null }) => void;
}> = ({ machines, machineId, moldId, mixBatchId, mixes, onChange }) => {
  const molds = machines.filter(isMold);
  const hosts = machines.filter(m => !isMold(m));
  const forMachine = mixes.filter(m => !machineId || !m.machineId || m.machineId === machineId);
  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField size="small" select fullWidth label="Máquina" value={machineId ?? ''} onChange={e => {
          const id = e.target.value === '' ? null : Number(e.target.value);
          const mounted = molds.find(m => m.mountedOnId === id);
          const lastMix = mixes.find(m => m.machineId === id);
          onChange({ machineId: id, moldId: mounted?.id ?? null, mixBatchId: lastMix?.id ?? null });
        }}>
          <MenuItem value="">Sin máquina</MenuItem>
          {hosts.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
        </TextField>
        <TextField size="small" select fullWidth label="Matriz" value={moldId ?? ''} onChange={e => onChange({ moldId: e.target.value === '' ? null : Number(e.target.value) })}
          helperText={moldId && molds.find(m => m.id === moldId)?.mountedOnId === machineId ? 'Montada en la máquina' : ' '}>
          <MenuItem value="">Sin matriz</MenuItem>
          {molds.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
        </TextField>
      </Stack>
      <TextField size="small" select label="Mezcla cargada" value={mixBatchId ?? ''} onChange={e => onChange({ mixBatchId: e.target.value === '' ? null : Number(e.target.value) })}
        helperText="La última mezcla preparada para esta máquina. Si cambia la mezcla o la matriz, el lote se corta y empieza otro.">
        <MenuItem value="">Sin mezcla</MenuItem>
        {forMachine.map(m => <MenuItem key={m.id} value={m.id}>{m.code} · {m.mixRecipeName || m.productName || 'mezcla'} · {kg(m.totalKg)}</MenuItem>)}
      </TextField>
    </>
  );
};

type WeighForm = {
  productId: number | null; machineId: number | null; moldId: number | null; mixBatchId: number | null;
  mode: 'kg' | 'bags'; kg: string; tare: string; bags: string; shift: Shift; operatorName: string;
};

const WeighDialog: React.FC<{
  machines: Machine[]; configs: ProductLotConfig[]; openLots: ProductLot[]; onClose: () => void; onSaved: () => void;
}> = ({ machines, configs, openLots, onClose, onSaved }) => {
  const [mixes, setMixes] = useState<MixBatch[]>([]);
  const last = openLots[0];
  const [form, setForm] = useState<WeighForm>(() => ({
    productId: last?.productId ?? (configs.length === 1 ? configs[0].productId : null),
    machineId: last?.machineId ?? null, moldId: last?.moldId ?? null, mixBatchId: last?.mixBatchId ?? null,
    mode: 'kg', kg: '', tare: '', bags: '1', shift: currentShift(), operatorName: last?.operatorName || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<{ text: string; lot?: ProductLot; tone: 'success' | 'info' }[]>([]);
  const scale = useSerialScale();

  useEffect(() => { api.getMixBatches({ from: addDaysIso(todayIso(), -14) }).then(m => setMixes([...m].sort((a, b) => b.id - a.id))).catch(() => undefined); }, []);

  const config = configs.find(c => c.productId === form.productId);
  const net = Math.max(0, num(form.kg) - num(form.tare));
  const units = form.mode === 'bags'
    ? (config?.unitsPerBag ? num(form.bags) * config.unitsPerBag : 0)
    : (config?.unitGrams ? Math.round((net * 1000) / config.unitGrams) : 0);
  const current = openLots.find(l => l.productId === form.productId && (l.machineId ?? null) === form.machineId && !l.isTrial);

  const save = async () => {
    if (!form.productId) return;
    setSaving(true);
    setError(null);
    try {
      const body: WeighIntoLot = {
        productId: form.productId, machineId: form.machineId, moldId: form.moldId, mixBatchId: form.mixBatchId,
        shift: form.shift, operatorName: form.operatorName || null,
        ...(form.mode === 'bags'
          ? { bags: num(form.bags), weighSource: 'manual' }
          : { kg: net, grossKg: num(form.tare) > 0 ? num(form.kg) : null, tareKg: num(form.tare) > 0 ? num(form.tare) : null, weighSource: scale.state === 'connected' ? 'balanza' : 'manual' }),
      };
      const r = await api.weighIntoLot(body);
      const next: typeof results = [];
      if (r.closedPrevious) next.push({ tone: 'info', lot: r.closedPrevious, text: `Cambió la matriz o la mezcla: se cerró el lote ${r.closedPrevious.code} (${kg(r.closedPrevious.kg)}).` });
      next.push(r.lotClosed
        ? { tone: 'success', lot: r.lot, text: `Lote ${r.lot.code} completo: ${kg(r.lot.kg)}, ${fmtQty(r.lot.units)} u. Imprimí la etiqueta.` }
        : { tone: 'info', text: `+${fmtQty(r.weighing.units)} u. al lote ${r.lot.code}: va ${kg(r.lot.kg)}${r.lot.targetKg > 0 ? ` de ${kg(r.lot.targetKg)}` : ''}.` });
      setResults(prev => [...next, ...prev].slice(0, 6));
      const toPrint = [r.closedPrevious, r.lotClosed ? r.lot : null].filter(Boolean) as ProductLot[];
      if (toPrint.length) printLotLabels(toPrint);
      setForm(f => ({ ...f, kg: '', tare: f.tare }));
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar la pesada'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pesar a la salida de la máquina</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ConfigSelect configs={configs} value={form.productId} onChange={id => setForm({ ...form, productId: id })} />
          <MachineFields machines={machines} mixes={mixes} machineId={form.machineId} moldId={form.moldId} mixBatchId={form.mixBatchId}
            onChange={p => setForm(f => ({ ...f, ...p }))} />
          {current && (
            <Alert severity="info" icon={false}>
              Lote abierto <b>{current.code}</b>: {kg(current.kg)}{current.targetKg > 0 ? ` de ${kg(current.targetKg)}` : ''} · {fmtQty(current.units)} u.
            </Alert>
          )}
          <ToggleButtonGroup size="small" exclusive value={form.mode} onChange={(_, v) => v && setForm({ ...form, mode: v })}>
            <ToggleButton value="kg">Por peso</ToggleButton>
            <ToggleButton value="bags">Por bolsas</ToggleButton>
          </ToggleButtonGroup>
          {form.mode === 'kg' ? (
            <>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField size="small" label="Peso en la balanza" value={form.kg} onChange={e => setForm({ ...form, kg: e.target.value })} autoFocus
                  InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} />
                <TextField size="small" label="Tara (bolsa / cajón)" value={form.tare} onChange={e => setForm({ ...form, tare: e.target.value })} sx={{ width: 170 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} helperText="Queda para la próxima" />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {scale.state === 'connected' ? (
                  <>
                    <Button size="small" variant="outlined" disabled={!scale.reading} onClick={() => scale.reading && setForm(f => ({ ...f, kg: String(scale.reading!.kg) }))}>
                      Tomar peso de la balanza{scale.reading ? ` (${kg(scale.reading.kg)}${scale.reading.stable ? '' : ', moviéndose'})` : ''}
                    </Button>
                    <Button size="small" onClick={() => scale.disconnect()}>Desconectar</Button>
                  </>
                ) : scale.state !== 'unsupported' ? (
                  <Button size="small" onClick={() => scale.connect()}>Conectar balanza por cable</Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">La balanza por cable funciona en Chrome o Edge.</Typography>
                )}
                {scale.error && <Typography variant="caption" color="error">{scale.error}</Typography>}
              </Stack>
            </>
          ) : (
            <TextField size="small" label={`Cantidad de ${config?.bagName || 'bolsa'}s`} value={form.bags} onChange={e => setForm({ ...form, bags: e.target.value })}
              helperText={config?.unitsPerBag ? `${fmtQty(config.unitsPerBag)} u. por ${config.bagName || 'bolsa'}` : 'Falta cargar las unidades por bolsa en Configuración'} />
          )}
          <Typography variant="body2">
            {units > 0 ? <>Suma <b>{fmtQty(units)} unidades</b>{form.mode === 'kg' ? ` (${kg(net)} neto)` : ''} al stock y al lote.</> : <span style={{ color: '#888' }}>Cargá el peso o las bolsas.</span>}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField size="small" select label="Turno" value={form.shift} onChange={e => setForm({ ...form, shift: e.target.value as Shift })} sx={{ width: 140 }}>
              {SHIFTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField size="small" fullWidth label="Operario" value={form.operatorName} onChange={e => setForm({ ...form, operatorName: e.target.value })} />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {results.map((r, i) => (
            <Alert key={i} severity={r.tone} action={r.lot ? <Button size="small" startIcon={<Print />} onClick={() => printLotLabels([r.lot!])}>Etiqueta</Button> : undefined}>
              {r.text}
            </Alert>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Listo</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.productId || units <= 0}>Registrar pesada</Button>
      </DialogActions>
    </Dialog>
  );
};

const TrialDialog: React.FC<{ machines: Machine[]; configs: ProductLotConfig[]; onClose: () => void; onSaved: () => void }> = ({ machines, configs, onClose, onSaved }) => {
  const [mixes, setMixes] = useState<MixBatch[]>([]);
  const [form, setForm] = useState({ productId: null as number | null, machineId: null as number | null, moldId: null as number | null, mixBatchId: null as number | null, targetKg: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.getMixBatches({ from: addDaysIso(todayIso(), -14) }).then(m => setMixes([...m].sort((a, b) => b.id - a.id))).catch(() => undefined); }, []);
  const save = async () => {
    if (!form.productId) return;
    try {
      await api.openProductLot({ productId: form.productId, machineId: form.machineId, moldId: form.moldId, mixBatchId: form.mixBatchId, isTrial: true, targetKg: num(form.targetKg) || 0, notes: form.notes || null });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo abrir el lote'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Lote de prueba</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Para una tirada con características especiales (otra mezcla, un color, una prueba de matriz). Las pesadas se le cargan eligiéndolo; no recibe las pesadas comunes.
          </Typography>
          <ConfigSelect configs={configs} value={form.productId} onChange={id => setForm({ ...form, productId: id })} />
          <MachineFields machines={machines} mixes={mixes} machineId={form.machineId} moldId={form.moldId} mixBatchId={form.mixBatchId} onChange={p => setForm(f => ({ ...f, ...p }))} />
          <TextField size="small" label="Peso del lote" value={form.targetKg} onChange={e => setForm({ ...form, targetKg: e.target.value })}
            InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} helperText="Vacío = se cierra a mano" />
          <TextField size="small" label="Qué tiene de especial" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} required />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!form.productId || !form.notes.trim()}>Abrir lote de prueba</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Armar pallet ====================

const PalletDialog: React.FC<{
  machines: Machine[]; configs: ProductLotConfig[]; locations: StorageLocation[]; onClose: () => void; onSaved: (t: ProductLotTrace) => void;
}> = ({ machines, configs, locations, onClose, onSaved }) => {
  const [productId, setProductId] = useState<number | null>(null);
  const [recipe, setRecipe] = useState<Recipe | null | undefined>(undefined);
  const [available, setAvailable] = useState<ProductLot[]>([]);
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [form, setForm] = useState({ mode: 'units' as 'units' | 'bags', units: '', bags: '', scrap: '', machineId: null as number | null, shift: currentShift(), operatorName: '', locationId: null as number | null, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const loadRecipe = useCallback(async (id: number) => {
    setRecipe(undefined);
    const r = await api.getRecipes({ productId: id, pageSize: 1 });
    setRecipe(r.recipes[0] ?? null);
  }, []);
  useEffect(() => {
    setPicks({});
    if (!productId) { setRecipe(undefined); return; }
    loadRecipe(productId).catch(() => setRecipe(null));
  }, [productId, loadRecipe]);
  const components = useMemo(() => (recipe?.items || []).filter(i => i.componentProductId && i.quantity > 0), [recipe]);
  useEffect(() => {
    if (!components.length) { setAvailable([]); return; }
    api.getProductLots({ kind: 'Insumo', available: true }).then(ls => setAvailable(ls.filter(l => components.some(c => c.componentProductId === l.productId)))).catch(() => setAvailable([]));
  }, [components]);

  const config = configs.find(c => c.productId === productId);
  const units = form.mode === 'bags' ? num(form.bags) * (config?.unitsPerBag || 0) : num(form.units);
  const produced = units + num(form.scrap);

  const save = async () => {
    if (!productId) return;
    setSaving(true);
    setError(null);
    try {
      const body: CreatePallet = {
        productId, units: form.mode === 'units' ? num(form.units) : null, bags: form.mode === 'bags' ? num(form.bags) : null,
        scrapUnits: num(form.scrap), machineId: form.machineId, shift: form.shift, operatorName: form.operatorName || null,
        locationId: form.locationId, consumeMaterials: false, notes: form.notes || null,
        lots: Object.entries(picks).filter(([, v]) => num(v) > 0).map(([id, v]) => ({ lotId: Number(id), units: num(v) })),
      };
      onSaved(await api.createPallet(body));
    } catch (e) {
      setError(errorMessage(e, 'No se pudo armar el pallet'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Armar pallet de producto terminado</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ProductSelect label="Producto terminado (ej. tapón con O-ring, tapón QR de una gasera)" value={productId} onChange={id => setProductId(id)} required />
          {productId && recipe === null && (
            <Alert severity="warning" action={<Button size="small" onClick={() => setEditing(true)}>Cargar componentes</Button>}>
              Este producto no tiene cargado qué lleva cada pieza.
            </Alert>
          )}
          {productId && recipe && (
            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>Qué consume</Typography>
                <Button size="small" startIcon={<Edit />} onClick={() => setEditing(true)}>Componentes</Button>
              </Stack>
              <Table size="small">
                <TableBody>
                  {components.map(c => {
                    const need = c.quantity * produced;
                    const inLots = available.filter(l => l.productId === c.componentProductId).reduce((s, l) => s + l.remainingUnits, 0);
                    return (
                      <TableRow key={c.componentProductId}>
                        <TableCell>{c.componentProductName}</TableCell>
                        <TableCell align="right">{c.quantity !== 1 ? `${c.quantity} × ` : ''}{fmtQty(need)} u.</TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color={inLots >= need ? 'text.secondary' : 'warning.main'}>
                            {inLots > 0 ? `${fmtQty(inLots)} en lotes${inLots < need ? ' (el resto sale sin lote)' : ''}` : 'sin lotes: sale del stock'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <ToggleButtonGroup size="small" exclusive value={form.mode} onChange={(_, v) => v && setForm({ ...form, mode: v })}>
              <ToggleButton value="units">Unidades</ToggleButton>
              <ToggleButton value="bags" disabled={!config?.unitsPerBag}>Bolsas</ToggleButton>
            </ToggleButtonGroup>
            {form.mode === 'units'
              ? <TextField size="small" label="Unidades buenas" value={form.units} onChange={e => setForm({ ...form, units: e.target.value })} />
              : <TextField size="small" label={`${config?.bagName || 'Bolsa'}s`} value={form.bags} onChange={e => setForm({ ...form, bags: e.target.value })} helperText={`${fmtQty(units)} u.`} />}
            <TextField size="small" label="Rotos al armar" value={form.scrap} onChange={e => setForm({ ...form, scrap: e.target.value })} sx={{ width: 150 }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" select fullWidth label="Máquina (O-ring, etiquetadora)" value={form.machineId ?? ''} onChange={e => setForm({ ...form, machineId: e.target.value === '' ? null : Number(e.target.value) })}>
              <MenuItem value="">—</MenuItem>
              {machines.filter(m => !isMold(m)).map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Turno" value={form.shift} onChange={e => setForm({ ...form, shift: e.target.value as Shift })} sx={{ minWidth: 130 }}>
              {SHIFTS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField size="small" fullWidth label="Operario" value={form.operatorName} onChange={e => setForm({ ...form, operatorName: e.target.value })} />
          </Stack>
          <TextField size="small" select label="Ubicación en el depósito" value={form.locationId ?? ''} onChange={e => setForm({ ...form, locationId: e.target.value === '' ? null : Number(e.target.value) })}
            helperText={locations.length ? 'Rack o estante donde queda el pallet' : 'Todavía no hay ubicaciones: se cargan en Stock → Depósito'}>
            <MenuItem value="">Sin ubicar</MenuItem>
            {locations.filter(l => l.kind !== 'Insumos').map(l => <MenuItem key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ''}</MenuItem>)}
          </TextField>

          {available.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Lotes que entran (opcional)</Typography>
              <Typography variant="caption" color="text.secondary">Si no elegís, se usan los más viejos primero (cerrados antes que abiertos).</Typography>
              <Table size="small">
                <TableBody>
                  {available.map(l => (
                    <TableRow key={l.id}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={picks[l.id] != null} onChange={e => setPicks(p => {
                          const next = { ...p };
                          if (e.target.checked) next[l.id] = String(l.remainingUnits); else delete next[l.id];
                          return next;
                        })} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{l.code}{l.isTrial ? ' (prueba)' : ''}</TableCell>
                      <TableCell>{l.productName}</TableCell>
                      <TableCell>{l.machineName || '—'}</TableCell>
                      <TableCell align="right">{fmtQty(l.remainingUnits)} u.</TableCell>
                      <TableCell sx={{ width: 130 }}>
                        {picks[l.id] != null && <TextField size="small" value={picks[l.id]} onChange={e => setPicks(p => ({ ...p, [l.id]: e.target.value }))} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <TextField size="small" label="Observaciones" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || !productId || !recipe || units <= 0}>Armar e imprimir etiqueta</Button>
      </DialogActions>
      {editing && productId && (
        <ComponentsDialog productId={productId} recipe={recipe ?? null} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadRecipe(productId).catch(() => undefined); }} />
      )}
    </Dialog>
  );
};

/** Qué lleva cada pieza del terminado. Edita la formulación activa sin tocar la materia prima que ya tenga cargada. */
const ComponentsDialog: React.FC<{ productId: number; recipe: Recipe | null; onClose: () => void; onSaved: () => void }> = ({ productId, recipe, onClose, onSaved }) => {
  const products = useProducts();
  const product = products.find(p => p.id === productId);
  const [rows, setRows] = useState<{ componentProductId: number | null; quantity: string }[]>(() => {
    const current = (recipe?.items || []).filter(i => i.componentProductId).map(i => ({ componentProductId: i.componentProductId!, quantity: String(i.quantity) }));
    return current.length ? current : [{ componentProductId: null, quantity: '1' }];
  });
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const productItems = rows.filter(r => r.componentProductId && num(r.quantity) > 0).map(r => ({ componentProductId: r.componentProductId!, quantity: num(r.quantity) }));
    if (productItems.some(i => i.componentProductId === productId)) { setError('Un producto no puede llevarse a sí mismo'); return; }
    // Se conservan los renglones que no son productos (materia prima, servicios, insumos) tal cual están.
    const others = (recipe?.items || []).filter(i => !i.componentProductId).map(i => ({
      componentMaterialId: i.componentMaterialId, componentServiceId: i.componentServiceId, componentInsumoId: i.componentInsumoId,
      quantity: i.quantity, yieldQuantity: i.yieldQuantity, unit: i.unit, supplierId: i.supplierId, procurementLeadDays: i.procurementLeadDays,
    }));
    try {
      const payload = { productId, name: recipe?.name || product?.name || 'Armado', manufacturingTimeHours: recipe?.manufacturingTimeHours ?? 0, notes: recipe?.notes, items: [...productItems, ...others] };
      if (recipe) await api.updateRecipe(recipe.id, payload); else await api.createRecipe(payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Qué lleva cada {product?.name || 'pieza'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Los insumos que se consumen por cada unidad terminada. Ej.: tapón cuadrado con O-ring = 1 tapón cuadrado + 1 O-ring; tapón QR de una gasera = 1 tapón QR + 1 etiqueta QR de esa gasera.
          </Typography>
          {rows.map((r, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}><ProductSelect label="Insumo" value={r.componentProductId} onChange={id => setRows(rs => rs.map((x, j) => (j === i ? { ...x, componentProductId: id } : x)))} /></Box>
              <TextField size="small" label="Por unidad" value={r.quantity} onChange={e => setRows(rs => rs.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} sx={{ width: 110 }} />
              <IconButton size="small" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Box><Button size="small" startIcon={<Add />} onClick={() => setRows(rs => [...rs, { componentProductId: null, quantity: '1' }])}>Agregar insumo</Button></Box>
          {(recipe?.items || []).some(i => !i.componentProductId) && (
            <Typography variant="caption" color="text.secondary">La materia prima de la formulación ({recipe!.items.filter(i => !i.componentProductId).map(i => i.componentMaterialName || i.componentInsumoName || i.componentServiceName).filter(Boolean).join(', ')}) se mantiene igual.</Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Despacho ====================

const DispatchDialog: React.FC<{ pallet: ProductLot; onClose: () => void; onSaved: () => void }> = ({ pallet, onClose, onSaved }) => {
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api.dispatchPallet(pallet.id, { customerId: customer?.id ?? null, note: note || null });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo despachar'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Despachar {pallet.code}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {fmtQty(pallet.units)} u. de {pallet.productName}. Sale de {pallet.locationCode || 'la planta'}; el stock lo descuenta la entrega o la venta.
          </Typography>
          <CustomerPicker value={customer} onChange={setCustomer} />
          <TextField size="small" label="Planta, remito o transporte" value={note} onChange={e => setNote(e.target.value)} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Despachar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Trazabilidad ====================

const LotTraceDialog: React.FC<{ trace: ProductLotTrace; onClose: () => void; onOpen: (id: number) => void; onChanged: () => void }> = ({ trace, onClose, onOpen, onChanged }) => {
  const [mixId, setMixId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState(trace);
  useEffect(() => setCurrent(trace), [trace]);
  const l = current.lot;
  const pallet = l.kind === 'Pallet';
  const removeWeighing = async (id: number) => {
    if (!window.confirm('¿Anular esta pesada? Sus unidades salen del stock y del lote.')) return;
    try {
      await api.deleteLotWeighing(id);
      onChanged();
      setCurrent(await api.traceProductLot(l.id));
    } catch (e) {
      setError(errorMessage(e, 'No se pudo anular'));
    }
  };
  const facts: [string, React.ReactNode][] = pallet
    ? [
        ['Producto', l.productName], ['Unidades', `${fmtQty(l.units)}${l.scrapUnits ? ` (+${fmtQty(l.scrapUnits)} rotos)` : ''}`],
        ['Armado', `${fmtDateTime(l.openedAt)}${l.machineName ? ` · ${l.machineName}` : ''}${l.shift ? ` · ${l.shift}` : ''}${l.operatorName ? ` · ${l.operatorName}` : ''}`],
        ['Ubicación', l.locationCode || '—'],
        ['Despacho', l.dispatchedAt ? `${fmtDateTime(l.dispatchedAt)} · ${l.customerName || 'sin cliente'}${l.dispatchNote ? ` · ${l.dispatchNote}` : ''}` : 'En depósito'],
      ]
    : [
        ['Producto', l.productName], ['Máquina · matriz', [l.machineName, l.moldName].filter(Boolean).join(' · ') || '—'],
        ['Mezcla', l.mixBatchId ? <Button size="small" sx={{ p: 0, minWidth: 0 }} onClick={() => setMixId(l.mixBatchId!)}>{l.mixBatchCode}</Button> : '—'],
        ['Peso', `${kg(l.kg)}${l.targetKg ? ` de ${kg(l.targetKg)}` : ''} · ${fmtQty(l.units)} u. · quedan ${fmtQty(l.remainingUnits)}`],
        ['Abierto / cerrado', `${fmtDateTime(l.openedAt)} → ${l.closedAt ? fmtDateTime(l.closedAt) : 'abierto'}`],
      ];
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ fontFamily: 'monospace' }}>{l.code}</Box>
        <StatusPill tone={STATUS_TONE[l.status]}>{pallet && l.status === 'Cerrado' ? 'En depósito' : l.status}</StatusPill>
        {l.isTrial && <StatusPill tone="warn">Prueba</StatusPill>}
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<Print />} onClick={() => printLotLabels([l])}>Etiqueta</Button>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {facts.map(([k, v]) => <TableRow key={k}><TableCell sx={{ color: 'text.secondary', width: 170 }}>{k}</TableCell><TableCell>{v}</TableCell></TableRow>)}
          </TableBody>
        </Table>

        {pallet && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Lo que lleva</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                {current.inputs.map((i, k) => (
                  <TableRow key={k}>
                    <TableCell>{i.productName}</TableCell>
                    <TableCell>{i.sourceLotId ? <Button size="small" sx={{ fontFamily: 'monospace' }} onClick={() => onOpen(i.sourceLotId!)}>{i.sourceLotCode}</Button> : <Typography variant="caption" color="text.secondary">sin lote (stock previo)</Typography>}</TableCell>
                    <TableCell align="right">{fmtQty(i.units)} u.</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {current.mixBatchIds.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">Mezclas detrás de este pallet:</Typography>
                {current.mixBatchIds.map(id => <Button key={id} size="small" variant="outlined" onClick={() => setMixId(id)}>Ver mezcla #{id}</Button>)}
              </Stack>
            )}
          </>
        )}

        {!pallet && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Pesadas</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow><TableCell>Hora</TableCell><TableCell align="right">Kg</TableCell><TableCell align="right">Unidades</TableCell><TableCell>Turno · operario</TableCell><TableCell>Origen</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {current.weighings.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>Sin pesadas.</TableCell></TableRow>}
                {current.weighings.map(w => (
                  <TableRow key={w.id}>
                    <TableCell>{fmtDateTime(w.at)}</TableCell>
                    <TableCell align="right">{kg(w.kg)}{w.tareKg ? <Typography variant="caption" color="text.secondary" display="block">bruto {kg(w.grossKg)} − tara {kg(w.tareKg)}</Typography> : null}</TableCell>
                    <TableCell align="right">{fmtQty(w.units)}{w.bags ? <Typography variant="caption" color="text.secondary" display="block">{w.bags} bolsas</Typography> : null}</TableCell>
                    <TableCell>{[w.shift, w.operatorName].filter(Boolean).join(' · ') || '—'}</TableCell>
                    <TableCell>{w.weighSource || '—'}</TableCell>
                    <TableCell align="right">{l.remainingUnits >= w.units && <IconButton size="small" onClick={() => removeWeighing(w.id)}><Delete fontSize="small" /></IconButton>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Dónde terminó</Typography>
            {current.usedIn.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Todavía no se usó en ningún pallet.</Typography>
            ) : (
              <Table size="small">
                <TableBody>
                  {current.usedIn.map(u => (
                    <TableRow key={u.palletId}>
                      <TableCell><Button size="small" sx={{ fontFamily: 'monospace' }} onClick={() => onOpen(u.palletId)}>{u.palletCode}</Button></TableCell>
                      <TableCell>{u.productName}</TableCell>
                      <TableCell align="right">{fmtQty(u.units)} u.</TableCell>
                      <TableCell>{u.status === 'Despachado' ? `${u.customerName || 'Despachado'} · ${fmtDateTime(u.dispatchedAt)}` : 'En depósito'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
      {mixId && <TraceDialog kind="mix" id={mixId} onClose={() => setMixId(null)} />}
    </Dialog>
  );
};

// ==================== Configuración ====================

const emptyConfig: ProductLotConfig = { productId: 0, lotKg: 1000, unitGrams: 0, unitsPerBag: 2900, bagName: 'bolsa', isActive: true, notes: '' };

const ConfigTab: React.FC<{ configs: ProductLotConfig[]; reload: () => Promise<void> }> = ({ configs, reload }) => {
  const [form, setForm] = useState<(Omit<ProductLotConfig, 'lotKg' | 'unitGrams' | 'unitsPerBag'> & { lotKg: string; unitGrams: string; unitsPerBag: string; isNew: boolean }) | null>(null);
  const [components, setComponents] = useState<{ productId: number; recipe: Recipe | null } | null>(null);
  const [pickProduct, setPickProduct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const edit = (c: ProductLotConfig, isNew = false) => setForm({ ...c, lotKg: String(c.lotKg), unitGrams: c.unitGrams ? String(c.unitGrams) : '', unitsPerBag: c.unitsPerBag ? String(c.unitsPerBag) : '', isNew });
  const save = async () => {
    if (!form?.productId) return;
    try {
      await api.saveLotConfig({ ...form, lotKg: num(form.lotKg), unitGrams: num(form.unitGrams), unitsPerBag: num(form.unitsPerBag) });
      setForm(null);
      setError(null);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };
  const remove = async (c: ProductLotConfig) => {
    if (!window.confirm(`¿Dejar de lotear ${c.productName}? Los lotes que ya existen quedan.`)) return;
    await api.deleteLotConfig(c.productId);
    reload();
  };
  const openComponents = async () => {
    if (!pickProduct) return;
    const r = await api.getRecipes({ productId: pickProduct, pageSize: 1 });
    setComponents({ productId: pickProduct, recipe: r.recipes[0] ?? null });
  };
  const unitsPreview = form && num(form.unitGrams) > 0 ? Math.round((num(form.lotKg) * 1000) / num(form.unitGrams)) : null;

  return (
    <>
      <Section
        title="Productos que se lotean a la salida de máquina"
        description="Insumos que salen de una inyectora y se pesan: tapón cuadrado, tapón QR sin etiqueta, O-ring."
        actions={<Button size="small" startIcon={<Add />} onClick={() => edit(emptyConfig, true)}>Agregar producto</Button>}
      >
        {configs.length === 0 ? (
          <EmptyState title="Ningún producto se lotea todavía" hint="Agregá el tapón con su peso por pieza: con eso la balanza ya cuenta unidades." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Lote</TableCell>
                  <TableCell align="right">Peso por pieza</TableCell>
                  <TableCell align="right">Por bolsa</TableCell>
                  <TableCell align="right">Unidades por lote</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map(c => (
                  <TableRow key={c.productId} sx={{ opacity: c.isActive ? 1 : 0.5 }}>
                    <TableCell>{c.productName}</TableCell>
                    <TableCell align="right">{c.lotKg > 0 ? kg(c.lotKg) : 'a mano'}</TableCell>
                    <TableCell align="right">{c.unitGrams ? `${c.unitGrams.toLocaleString('es-AR')} g` : '—'}</TableCell>
                    <TableCell align="right">{c.unitsPerBag ? `${fmtQty(c.unitsPerBag)} u.` : '—'}{c.kgPerBag ? <Typography variant="caption" color="text.secondary" display="block">{kg(c.kgPerBag)}</Typography> : null}</TableCell>
                    <TableCell align="right">{fmtQty(c.unitsPerLot)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => edit(c)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(c)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section title="Qué lleva cada producto terminado" description="Lo que consume el armado de un pallet. El tapón QR va uno por gasera: cada etiqueta QR es de una empresa." padded>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: 1 }}><ProductSelect label="Producto terminado" value={pickProduct} onChange={id => setPickProduct(id)} /></Box>
          <Button variant="outlined" onClick={openComponents} disabled={!pickProduct}>Ver / editar componentes</Button>
        </Stack>
      </Section>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.isNew ? 'Lotear un producto' : `Loteo de ${form?.productName}`}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {form.isNew && <ProductSelect label="Producto (insumo)" value={form.productId || null} onChange={id => setForm({ ...form, productId: id ?? 0 })} required />}
              <TextField size="small" label="Peso fijo del lote" value={form.lotKg} onChange={e => setForm({ ...form, lotKg: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} helperText="Al llegar se cierra y se imprime la etiqueta. 0 = se cierra a mano." />
              <TextField size="small" label="Peso de una pieza" value={form.unitGrams} onChange={e => setForm({ ...form, unitGrams: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                helperText={unitsPreview ? `Un lote de ${kg(num(form.lotKg))} son ${fmtQty(unitsPreview)} piezas` : 'Pasa los kg de la balanza a unidades'} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Unidades por bolsa" value={form.unitsPerBag} onChange={e => setForm({ ...form, unitsPerBag: e.target.value })} helperText="Tapón: 2.900 (planilla)" />
                <TextField size="small" label="Nombre" value={form.bagName || ''} onChange={e => setForm({ ...form, bagName: e.target.value })} sx={{ width: 120 }} />
              </Stack>
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activo" />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.productId}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {components && (
        <ComponentsDialog productId={components.productId} recipe={components.recipe} onClose={() => setComponents(null)} onSaved={() => setComponents(null)} />
      )}
    </>
  );
};

export default FactoryProductLotsPage;
